import { Box } from '@mui/material';

/** The client fields this strip shows; everything is optional, as the legacy table has it. */
export interface ClientDetails {
    ClNr: number;
    Nume: string;
    Oras?: string | null;
    Str?: string | null;
    Nr?: string | null;
    Ap?: string | null;
    Jud?: string | null;
    CNP?: string | null;
    Telefon?: string | null;
    Notite?: string | null;
    Observatii?: string | null;
}

interface ClientDetailsRowProps {
    /** The selected client, or null: with none selected the strip is not rendered at all. */
    client: ClientDetails | null;
}

/** The column widths, shared by the header strip and the values strip below it. */
const GRID_COLUMNS = '60px 150px 100px 100px 50px 30px 40px 100px 80px 60px 100px 100px 100px';

const HEADINGS = [
    'ClNr',
    'Nume',
    'Oras',
    'Str',
    'Nr',
    'Ap',
    'Jud',
    'CNP',
    'Categorie',
    'BS',
    'Telefon',
    'Politia',
    'Observatii',
];

/** An empty field reads as a dash, never as a blank cell. */
const orDash = (value: string | number | null | undefined) => {
    const text = String(value ?? '').trim();
    return text || '-';
};

/**
 * The one-line client card between the client grid and the contracts grid: a heading strip and
 * a values strip, in the legacy column order.
 *
 * Its own component because the two strips were a dozen "this field or a dash" decisions inside
 * the Contracts page, which counted against that page's cognitive complexity while depending on
 * nothing but the selected client.
 */
const ClientDetailsRow = ({ client }: ClientDetailsRowProps) => {
    if (!client) {
        return null;
    }

    const notes = client.Notite ?? client.Observatii ?? '';

    return (
        <>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: GRID_COLUMNS,
                    gap: 1,
                    fontSize: '0.75rem',
                    p: 1,
                    backgroundColor: '#f9f9f9',
                    border: '1px solid #e0e0e0',
                }}
            >
                {HEADINGS.map(heading => (
                    <Box key={heading} sx={{ fontWeight: 'bold' }}>
                        {heading}
                    </Box>
                ))}
            </Box>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: GRID_COLUMNS,
                    gap: 1,
                    fontSize: '0.75rem',
                    p: 1,
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderTop: 'none',
                }}
            >
                <Box>{client.ClNr}</Box>
                <Box>{client.Nume}</Box>
                <Box>{orDash(client.Oras)}</Box>
                <Box>{orDash(client.Str)}</Box>
                <Box>{orDash(client.Nr)}</Box>
                <Box>{orDash(client.Ap)}</Box>
                <Box>{orDash(client.Jud)}</Box>
                <Box>{orDash(client.CNP)}</Box>
                <Box>VIP</Box>
                <Box>-</Box>
                <Box>{orDash(client.Telefon)}</Box>
                <Box>-</Box>
                <Box
                    data-testid="client-file-observations"
                    title={String(notes)}
                    sx={{
                        minWidth: 0,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {orDash(notes)}
                </Box>
            </Box>
        </>
    );
};

export default ClientDetailsRow;
